import { Component, Inject, Input, OnInit, OnChanges, SimpleChanges, DestroyRef } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import {
    assignModelOptions,
    isDefined,
    NotificationIdentifierModel,
    TextModel,
    TextModelOptions,
    TimesheetAndExpensesP58Model,
    TimesheetModel,
    TimesheetAndExpensesInstanceToken,
    TimesheetAndExpensesInterface,
} from '@evorait/pace-core';
import { faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { BehaviorSubject, iif, Observable } from 'rxjs';
import { map, mergeMap, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-expenses',
    templateUrl: './expenses.component.html',
    styleUrls: ['./expenses.component.scss'],
    standalone: false,
})
export class ExpensesComponent implements OnInit, OnChanges {
    @Input()
    public model: TimesheetAndExpensesP58Model;

    @Input()
    public parentForm: FormArray;

    public expensesForm: FormGroup;
    public expensesFormChildren: FormArray;

    public expensesEntriesFormChildren: { [key: string]: FormArray } = {};

    public distance: { [key: string]: TextModel } = {};
    public notificationInfo$: Observable<{ [key: string]: ExpenseInfo }>;

    public faChevronDown = faChevronDown;
    public faChevronRight = faChevronRight;

    // Using BehaviorSubject here, so that the initial trigger is not lost.
    // The observable is subscribed in the template via *ngrxLet, which subscribes
    // after ngOnInit runs. A plain Subject would miss emissions made before subscription.
    private triggerNotificationInfoRefresh = new BehaviorSubject<void>(undefined);

    public constructor(
        private readonly translate: TranslateService,
        private readonly formBuilder: FormBuilder,
        @Inject(TimesheetAndExpensesInstanceToken)
        private readonly timesheetAndExpensesService: TimesheetAndExpensesInterface,
        private readonly destroyRef: DestroyRef,
    ) {}

    public ngOnInit(): void {
        this.expensesFormChildren = this.formBuilder.array([]);
        this.expensesForm = this.formBuilder.group({
            children: this.expensesFormChildren,
        });

        this.notificationInfo$ = this.triggerNotificationInfoRefresh.asObservable().pipe(
            takeUntilDestroyed(this.destroyRef),
            mergeMap(() => this.setNotificationInfo$()),
        );

        this.triggerNotificationInfoRefresh.next();

        const expensesDictionary = this.model.expensesDictionary;

        // we need to create for each entry in the expenses dictionary to be able to validate individual the entries.
        // we need to loop with a forin because the expensesDictionary is a Object.
        for (const expensesDictionaryKey in expensesDictionary) {
            if (expensesDictionary.hasOwnProperty(expensesDictionaryKey)) {
                const entryExpensesFormChildren = this.formBuilder.array([]);
                this.expensesEntriesFormChildren[expensesDictionaryKey] = entryExpensesFormChildren;
                this.expensesFormChildren.push(entryExpensesFormChildren);
            }
        }

        this.parentForm.push(this.expensesForm);
    }

    // TODO: why call this refresh in ngOnChanges?
    public ngOnChanges(changes: SimpleChanges): void {
        if (changes.model && !changes.model.isFirstChange()) {
            this.triggerNotificationInfoRefresh.next();
        }
    }

    private setNotificationInfo$(): Observable<{ [p: string]: ExpenseInfo }> {
        return iif(
            () => this.model.readonly,
            this.timesheetAndExpensesService.selectSubmittedTimesheet$(),
            this.timesheetAndExpensesService.selectCurrentTimesheet$(),
        ).pipe(
            take(1),
            map((timesheet: Readonly<TimesheetModel>) => {
                return _.mapValues(this.model.expensesDictionary, (expenses, notificationIdString) => {
                    // create proper NotificationIdentifierModels from the strings used as indices in the expenses dictionary
                    const id = NotificationIdentifierModel.fromString(notificationIdString);
                    const timesheetNotification = timesheet.notifications.find(
                        (n) => n.isConfirmationSubmitted && n.notificationId.equalsTo(id),
                    );
                    // get the saved expenses list from the submitted notification.
                    if (this.model.readonly && timesheetNotification && timesheetNotification.expenses.length > 0) {
                        expenses.value = timesheetNotification.expenses;
                    }

                    // Distance values from confirmation screen for a notification would be readonly
                    if (id.qmNum) {
                        this.distance[notificationIdString] = new TextModel(
                            id.toString(),
                            timesheetNotification && timesheetNotification.distance
                                ? timesheetNotification.distance.value
                                : undefined,
                            new TextModelOptions({
                                readonly: true,
                                unit: <string>this.translate.instant('CONFIRMATION_EXPENSES_KM_LABEL'),
                            }),
                        );
                    } else if (this.model.readonly && timesheetNotification) {
                        this.model.additionalDistance = timesheetNotification.distance;
                        this.model.additionalDistance = assignModelOptions(
                            this.model.additionalDistance,
                            {
                                ...this.model.additionalDistance.options,
                                readonly: this.model.readonly,
                            },
                            this.model,
                        );
                    }
                    // create notification title string to be displayed as accordion header
                    let notificationTitle: string;
                    if (id.qmNum) {
                        notificationTitle = `${id.qmNum} : ${id.maNum}`;
                        if (timesheetNotification && timesheetNotification.customerName) {
                            notificationTitle += ` : ${timesheetNotification.customerName}`;
                        }
                    } else {
                        notificationTitle = <string>this.translate.instant('TIMESHEET_EXPENSES_ADDITIONAL');
                    }
                    return {
                        notification: id,
                        title: notificationTitle,
                        isOpen: false,
                    };
                });
            }),
        );
    }

    protected readonly isDefined = isDefined;
}

interface ExpenseInfo {
    notification: NotificationIdentifierModel;
    title: string;
    isOpen: boolean;
}
